/**
 * MyInterviewDetailPage.tsx
 * V55.37: 내 면담기록 상세 페이지
 *
 * URL: /my/interviews/:interviewId
 *
 * 기능:
 * - 면담 상세 내용 읽기 전용 표시
 * - 대상자 서명 입력
 * - 동의 (상호합의) / 미동의 (반송) 처리
 */

import React, { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Loader,
  AlertCircle,
  Check,
  X,
  FileText,
  User,
  MapPin,
  Calendar,
  Clock,
} from 'lucide-react';
import {
  useInterviewDetail,
  getSignatureUrl,
  agreeInterview,
  disagreeInterview,
  DELIVERY_STATUS_LABELS,
  DeliveryStatus,
} from '../../shared/hooks/useInterviews';
import { SignatureModal } from '../org-management/components/SignatureModal';

export function MyInterviewDetailPage() {
  const { interviewId } = useParams<{ interviewId: string }>();
  const navigate = useNavigate();

  // 면담 상세 조회
  const { interview, isLoading, error } = useInterviewDetail(interviewId || null);

  // 상태
  const [targetSignature, setTargetSignature] = useState<string | null>(null);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [showAgreeConfirm, setShowAgreeConfirm] = useState(false);
  const [showDisagreeModal, setShowDisagreeModal] = useState(false);
  const [disagreementReason, setDisagreementReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // 서명 이미지 URL 상태
  const [interviewerSignatureUrl, setInterviewerSignatureUrl] = useState<string | null>(null);
  const [targetSignatureUrl, setTargetSignatureUrl] = useState<string | null>(null);

  // 면담자 서명 URL 로드
  React.useEffect(() => {
    if (interview?.interviewerSignaturePath) {
      getSignatureUrl(interview.interviewerSignaturePath).then(setInterviewerSignatureUrl);
    }
  }, [interview?.interviewerSignaturePath]);

  // 대상자 서명 URL 로드
  React.useEffect(() => {
    if (interview?.targetSignaturePath) {
      getSignatureUrl(interview.targetSignaturePath).then(setTargetSignatureUrl);
    }
  }, [interview?.targetSignaturePath]);

  // 면담 일시 포맷팅
  const formattedDateTime = useMemo(() => {
    if (!interview?.interviewDate) return '-';
    const dateStr = interview.interviewDate.replace(/-/g, '.');
    const timeStr = interview.interviewTime || '00:00';
    return `${dateStr} ${timeStr}`;
  }, [interview?.interviewDate, interview?.interviewTime]);

  // 상태 배지 색상
  const statusColors: Record<DeliveryStatus, { color: string; bg: string }> = {
    notSent: { color: '#64748b', bg: '#f1f5f9' },
    sent: { color: '#3b82f6', bg: '#dbeafe' },
    agreed: { color: '#22c55e', bg: '#dcfce7' },
    disagreed: { color: '#ef4444', bg: '#fee2e2' },
  };

  // 동의 처리
  const handleAgree = async () => {
    if (!interview) return;

    // 서명 필수 체크
    const hasSignature = targetSignature || interview.targetSignaturePath;
    if (!hasSignature) {
      alert('서명을 진행해주세요.');
      setShowAgreeConfirm(false);
      return;
    }

    setIsSubmitting(true);
    try {
      await agreeInterview(
        interview.id,
        targetSignature || undefined
      );
      alert('면담에 동의하였습니다.');
      navigate('/my/interviews');
    } catch (err: any) {
      alert(err.message || '동의 처리에 실패했습니다.');
    } finally {
      setIsSubmitting(false);
      setShowAgreeConfirm(false);
    }
  };

  // 미동의 처리
  const handleDisagree = async () => {
    if (!interview) return;

    if (!disagreementReason.trim()) {
      alert('미동의 사유를 입력해주세요.');
      return;
    }

    setIsSubmitting(true);
    try {
      await disagreeInterview(interview.id, disagreementReason.trim());
      alert('미동의 사유가 전송되었습니다.');
      navigate('/my/interviews');
    } catch (err: any) {
      alert(err.message || '미동의 처리에 실패했습니다.');
    } finally {
      setIsSubmitting(false);
      setShowDisagreeModal(false);
    }
  };

  // 서명 저장
  const handleSignatureSave = (base64: string) => {
    setTargetSignature(base64);
    setShowSignatureModal(false);
  };

  // 로딩 UI
  if (isLoading) {
    return (
      <div style={{ padding: '24px' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '300px',
            color: '#64748b',
          }}
        >
          <Loader size={24} style={{ animation: 'spin 1s linear infinite' }} />
          <span style={{ marginLeft: '8px' }}>로딩 중...</span>
        </div>
      </div>
    );
  }

  // 에러 UI
  if (error || !interview) {
    return (
      <div style={{ padding: '24px' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '16px',
            background: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '8px',
            color: '#b91c1c',
          }}
        >
          <AlertCircle size={20} />
          <div>
            <div style={{ fontWeight: 600 }}>면담 기록을 불러올 수 없습니다</div>
            <div style={{ fontSize: '14px', marginTop: '4px' }}>
              {error?.message || '요청한 면담 기록이 존재하지 않습니다.'}
            </div>
          </div>
          <button
            onClick={() => navigate('/my/interviews')}
            style={{
              marginLeft: 'auto',
              padding: '8px 16px',
              background: '#fee2e2',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '13px',
            }}
          >
            목록으로
          </button>
        </div>
      </div>
    );
  }

  const deliveryStatus = (interview.deliveryStatus || 'notSent') as DeliveryStatus;
  const { color: statusColor, bg: statusBg } = statusColors[deliveryStatus] || statusColors.sent;
  const canAgreeOrDisagree = deliveryStatus === 'sent';

  return (
    <div style={{ padding: '24px', maxWidth: '900px', margin: '0 auto' }}>
      {/* 뒤로가기 */}
      <button
        onClick={() => navigate('/my/interviews')}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          padding: '8px 12px',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          color: '#64748b',
          marginBottom: '16px',
          fontSize: '14px',
        }}
      >
        <ArrowLeft size={18} />
        목록으로
      </button>

      {/* 타이틀 + 상태 */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '24px',
        }}
      >
        <h1 style={{ fontSize: '24px', fontWeight: 'bold', color: '#1e293b' }}>
          {interview.targetUserName} 면담 기록
        </h1>
        <span
          style={{
            padding: '6px 14px',
            borderRadius: '6px',
            fontSize: '13px',
            fontWeight: 600,
            color: statusColor,
            background: statusBg,
          }}
        >
          {DELIVERY_STATUS_LABELS[deliveryStatus]}
        </span>
      </div>

      {/* 면담 정보 (한 줄 표시) */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '24px',
          padding: '16px 20px',
          background: '#f8fafc',
          borderRadius: '8px',
          marginBottom: '24px',
          fontSize: '14px',
          color: '#475569',
        }}
      >
        {/* 면담일시 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <Calendar size={16} style={{ color: '#64748b' }} />
          <span style={{ color: '#94a3b8' }}>면담일시</span>
          <span style={{ fontWeight: 500, color: '#1e293b' }}>{formattedDateTime}</span>
        </div>

        <div style={{ width: '1px', height: '16px', background: '#e2e8f0' }} />

        {/* 면담장소 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <MapPin size={16} style={{ color: '#64748b' }} />
          <span style={{ color: '#94a3b8' }}>면담장소</span>
          <span style={{ fontWeight: 500, color: '#1e293b' }}>{interview.location || '-'}</span>
        </div>

        <div style={{ width: '1px', height: '16px', background: '#e2e8f0' }} />

        {/* 면담타입 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <FileText size={16} style={{ color: '#64748b' }} />
          <span style={{ color: '#94a3b8' }}>면담타입</span>
          <span style={{ fontWeight: 500, color: '#1e293b' }}>
            {/* V62: typeLabel(BE resolveTypeLabel → DB name 스냅샷) 전용 — "면담 목적 = 면담 타입명" 정책 */}
            {interview.typeLabel || '-'}
          </span>
        </div>
      </div>

      {/* 면담자/대상자 정보 */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: '24px',
          marginBottom: '24px',
        }}
      >
        {/* 면담자 (조직장) 정보 */}
        <div
          style={{
            background: 'white',
            border: '1px solid #e2e8f0',
            borderRadius: '12px',
            padding: '20px',
          }}
        >
          <h3 style={{ fontSize: '14px', fontWeight: 600, color: '#475569', marginBottom: '16px' }}>
            면담자
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div>
              <div style={{ fontSize: '12px', color: '#94a3b8' }}>소속</div>
              <div style={{ fontSize: '14px' }}>{interview.interviewerOrgUnitName || '-'}</div>
            </div>
            <div>
              <div style={{ fontSize: '12px', color: '#94a3b8' }}>성명</div>
              <div style={{ fontSize: '14px' }}>{interview.interviewerName || '-'}</div>
            </div>
            <div>
              <div style={{ fontSize: '12px', color: '#94a3b8' }}>서명</div>
              <div
                style={{
                  width: '120px',
                  height: '50px',
                  border: '1px solid #e2e8f0',
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: '#f8fafc',
                  marginTop: '4px',
                }}
              >
                {interviewerSignatureUrl ? (
                  <img
                    src={interviewerSignatureUrl}
                    alt="면담자 서명"
                    style={{ maxWidth: '100%', maxHeight: '100%' }}
                  />
                ) : (
                  <span style={{ fontSize: '12px', color: '#94a3b8' }}>-</span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* 대상자 (본인) 정보 */}
        <div
          style={{
            background: 'white',
            border: '1px solid #e2e8f0',
            borderRadius: '12px',
            padding: '20px',
          }}
        >
          <h3 style={{ fontSize: '14px', fontWeight: 600, color: '#475569', marginBottom: '16px' }}>
            대상자 (본인)
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div>
              <div style={{ fontSize: '12px', color: '#94a3b8' }}>소속</div>
              <div style={{ fontSize: '14px' }}>{interview.targetUserOrgUnitName || '-'}</div>
            </div>
            <div>
              <div style={{ fontSize: '12px', color: '#94a3b8' }}>성명</div>
              <div style={{ fontSize: '14px' }}>{interview.targetUserName || '-'}</div>
            </div>
            <div>
              <div style={{ fontSize: '12px', color: '#94a3b8' }}>서명</div>
              <div
                onClick={() => canAgreeOrDisagree && setShowSignatureModal(true)}
                style={{
                  width: '120px',
                  height: '50px',
                  border: '1px dashed #cbd5e1',
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: canAgreeOrDisagree ? 'pointer' : 'default',
                  background: targetSignature || targetSignatureUrl ? '#ffffff' : '#f8fafc',
                  marginTop: '4px',
                }}
              >
                {targetSignature ? (
                  <img
                    src={`data:image/png;base64,${targetSignature}`}
                    alt="대상자 서명"
                    style={{ maxWidth: '100%', maxHeight: '100%' }}
                  />
                ) : targetSignatureUrl ? (
                  <img
                    src={targetSignatureUrl}
                    alt="대상자 서명"
                    style={{ maxWidth: '100%', maxHeight: '100%' }}
                  />
                ) : canAgreeOrDisagree ? (
                  <span style={{ fontSize: '12px', color: '#3b82f6' }}>서명하기</span>
                ) : (
                  <span style={{ fontSize: '12px', color: '#94a3b8' }}>-</span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 면담 내용 */}
      <div
        style={{
          background: 'white',
          border: '1px solid #e2e8f0',
          borderRadius: '12px',
          padding: '24px',
          marginBottom: '24px',
        }}
      >
        <h3 style={{ fontSize: '14px', fontWeight: 600, color: '#475569', marginBottom: '16px' }}>
          면담 내용
        </h3>
        <div
          style={{
            fontSize: '14px',
            lineHeight: '1.7',
            color: '#334155',
          }}
          dangerouslySetInnerHTML={{ __html: interview.contentHtml || '' }}
        />
      </div>

      {/* 미동의 사유 (disagreed 상태일 때만 표시) */}
      {deliveryStatus === 'disagreed' && interview.disagreementReason && (
        <div
          style={{
            background: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '12px',
            padding: '20px',
            marginBottom: '24px',
          }}
        >
          <h3 style={{ fontSize: '14px', fontWeight: 600, color: '#dc2626', marginBottom: '12px' }}>
            미동의 사유
          </h3>
          <p style={{ fontSize: '14px', color: '#7f1d1d', whiteSpace: 'pre-wrap' }}>
            {interview.disagreementReason}
          </p>
        </div>
      )}

      {/* 동의/미동의 버튼 (sent 상태일 때만 표시) */}
      {canAgreeOrDisagree && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '16px',
            paddingTop: '16px',
            borderTop: '1px solid #e2e8f0',
          }}
        >
          <button
            onClick={() => setShowDisagreeModal(true)}
            disabled={isSubmitting}
            style={{
              padding: '12px 32px',
              background: 'white',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              fontSize: '15px',
              fontWeight: 600,
              color: '#64748b',
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <X size={18} />
            미동의
          </button>
          <button
            onClick={() => setShowAgreeConfirm(true)}
            disabled={isSubmitting}
            style={{
              padding: '12px 32px',
              background: '#22c55e',
              border: 'none',
              borderRadius: '8px',
              fontSize: '15px',
              fontWeight: 600,
              color: 'white',
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <Check size={18} />
            동의
          </button>
        </div>
      )}

      {/* 동의 확인 모달 */}
      {showAgreeConfirm && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '12px',
              padding: '24px',
              width: '400px',
              maxWidth: '90vw',
            }}
          >
            <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '16px' }}>
              면담 동의 확인
            </h3>
            <p style={{ fontSize: '14px', color: '#64748b', marginBottom: '24px' }}>
              본 면담 내용에 대해 동의하시면 상호 합의가 완료된 것으로 간주합니다.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
              <button
                onClick={() => setShowAgreeConfirm(false)}
                disabled={isSubmitting}
                style={{
                  padding: '10px 20px',
                  background: '#f1f5f9',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                }}
              >
                취소
              </button>
              <button
                onClick={handleAgree}
                disabled={isSubmitting}
                style={{
                  padding: '10px 20px',
                  background: '#22c55e',
                  border: 'none',
                  borderRadius: '6px',
                  color: 'white',
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: 600,
                }}
              >
                {isSubmitting ? '처리 중...' : '확인'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 미동의 모달 */}
      {showDisagreeModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '12px',
              padding: '24px',
              width: '480px',
              maxWidth: '90vw',
            }}
          >
            <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '16px' }}>
              미동의 사유 입력
            </h3>
            <p style={{ fontSize: '14px', color: '#64748b', marginBottom: '16px' }}>
              면담 내용에 동의하지 않는 사유를 입력해주세요.
            </p>
            <textarea
              value={disagreementReason}
              onChange={(e) => setDisagreementReason(e.target.value)}
              placeholder="미동의 사유를 입력해주세요..."
              style={{
                width: '100%',
                minHeight: '120px',
                padding: '12px',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                fontSize: '14px',
                resize: 'vertical',
                marginBottom: '20px',
              }}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
              <button
                onClick={() => {
                  setShowDisagreeModal(false);
                  setDisagreementReason('');
                }}
                disabled={isSubmitting}
                style={{
                  padding: '10px 20px',
                  background: '#f1f5f9',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                }}
              >
                취소
              </button>
              <button
                onClick={handleDisagree}
                disabled={isSubmitting || !disagreementReason.trim()}
                style={{
                  padding: '10px 20px',
                  background: '#ef4444',
                  border: 'none',
                  borderRadius: '6px',
                  color: 'white',
                  cursor: isSubmitting || !disagreementReason.trim() ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: 600,
                  opacity: disagreementReason.trim() ? 1 : 0.6,
                }}
              >
                {isSubmitting ? '처리 중...' : '전송'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 서명 모달 */}
      <SignatureModal
        isOpen={showSignatureModal}
        onClose={() => setShowSignatureModal(false)}
        onSave={handleSignatureSave}
      />
    </div>
  );
}

export default MyInterviewDetailPage;
